/*

inputs:
- we are given an amount of days `N` that represents the number of days we have to travel
- we are given a weighted bidirectional graph presented in a form of square matrix `distances`,
	where `distances[i][j]` is the distance between `i` and `j` nodes
	and some distances can be Infinity
- we are given a weighted bidirectional graph presented in a form of matrix `times`,
	where `times[i][j]` is the distance between `i` and `j` nodes
- we are given an integer value `targetSum` that represents the total sum of all trips in the array
- we are given an integer value `dailyTimeLimit` that represents the maximum time we can travel in a single day
- we are given an integer value `timeSpentInNode` that represents the time we have to spend in each node

our goal is to fill the array with graph trips with the following restrictions:
- every array element is either null or a single graph trip in the form of array `[i,j,k,i]` with the visited nodes
- every graph trip in the array starts and ends in the root node
- every node visited in the graph trip has to be visited for `timeSpentInNode` time
- each graph trip cannot be longer than the `dailyTimeLimit`
- total sum of all trips in the array should be as close as possible to the `targetSum`, but not higher
- the array should contain as many different trips as possible
- the array should contain as many trips as possible
- each node should be visited at least once over the course of `N` days if possible

output:
- return an array of graph trips that satisfies the above conditions
- every graph trip should be in the form of array `[i,j,k,i]` with the visited nodes

*/

/*

1. Укажи точку, откуда ты начинаешь работу (офис/дом)
root node

2. Укажи за какой период нужен отчёт
amount of days

3. Сколько миль или показатели одометра на начало и конец
targetSum

4. Укажи рабочие часы и дни (как в приложении) + галочка “Учитывать праздники”
dailyTimeLimit

5. Тип рабочих поездок:
	1. Офис - Клиент - Клиент - Клиент - Офис…
	standard

	2. Офис - Клиент - Офис - Клиент…
	client->client is always infinity

	3. Офис - Поставщик - Получатель - Поставщик - Получатель…
	supplier->supplier and customer->customer is always infinity

6. Показываем колонки типов адресов (Поставщики, Получатели, Офис/дом) в зависимости от выбора.
	Под каждой колонкой выбор “Импортировать / Добавить вручную / Сгенерировать”
distances/times arrays depend on this

7. Сколько бизнес поездок ты совершаешь в рабочий день? (от - до)
dailyTripsLimit

8. (Опционально) Сколько времени ты проводишь в точках и между клиентами:
	1. Офис: N часов N минут
	2. Клиент: от - до (часов, минут)
	3. Поставщик: от - до (часов, минут)

the best design would be to separate these three types of algo:
- root -> (client[]) x N -> root
- (root -> client[]) x N -> root
- root -> (supplier[] -> customer[]) x N -> root

can we abstract this further with constraints?

9. Генерируем, показываем списком или в календаре + саммари по пройденным милям и вычету за весь период.
10. (Опционально) Заполнить информацию о машине
11. Предлагаем экспортировать за деньги.
	1. Подписка может высчитываться по формуле (5% от размера налогового вычета, т.к. средняя налоговая ставка в США - 25%)

*/


/*
1. prepare distances (infinities etc)
2. prepare times

hard restrictions [before]:
- min/max daily trips
- work hours (daily time limit)

3. findNRandomPaths (find largest paths)

hard restrictions [after]:
- total sum of all trips equal or lower than targetSum

soft restrictions [after]:
- time of start and end of each trip (should end within work hours +- 15 minutes)

*/

import { findNRandomPaths, enrichData, validateInputsSimple, permsOf, pathDistance } from './algo'
import { last, repeat } from './array'

export function validateAlgo1Inputs(
	N: number,
	distances: number[][],
	times: number[][],
	targetSum: number,
	dailyTimeLimit: number,
	timeSpentInNodes: number[]
)
{
	validateInputsSimple(N, distances, targetSum, 0, Infinity)

	// Validate times matrix dimensions
	if (!times || !Array.isArray(times) || times.length !== distances.length)
		throw new Error('Times matrix must have same dimensions as distances matrix')

	for (let i = 0; i < times.length; i++)
	{
		let row = times[i]
		if (!Array.isArray(row) || row.length !== distances[i]!.length)
			throw new Error('Times matrix must have same dimensions as distances matrix')

		for (let j = 0; j < row.length; j++)
		{
			let val = row[j]
			if (typeof val !== 'number' || val < 0)
				throw new Error('Times matrix must contain non-negative numbers')

			// If distance is Infinity, time should also be Infinity
			if (distances[i]![j] === Infinity && val !== Infinity)
				throw new Error('Impossible routes must have Infinity time')
		}
	}

	// Validate time constraints
	if (typeof dailyTimeLimit !== 'number' || dailyTimeLimit <= 0)
		throw new Error('Daily time limit must be a positive number')

	if (!Array.isArray(timeSpentInNodes) || timeSpentInNodes.length != distances.length)
		throw new Error('Time spent in nodes must be an array matching distances matrix dimensions')

	for (let time of timeSpentInNodes)
	{
		if (typeof time !== 'number' || time < 0)
			throw new Error('Time spent in node must be a positive number')

		if (time >= dailyTimeLimit)
			throw new Error('Time spent in node must be less than daily time limit to allow for travel time')
	}
}

/**
 * Generates an optimized set of trips for a private tutor visiting students.
 * 
 * @param N - Number of days to generate trips for
 * @param distances - Matrix of distances between nodes where distances[i][j] is distance from node i to j
 * @param times - Matrix of travel times between nodes where times[i][j] is time to travel from node i to j
 * @param targetSum - Target total distance for all trips combined
 * @param dailyTimeLimit - Maximum time allowed for each day's trips
 * @param timeSpentInNodes - Array specifying time spent at each node
 * @returns Object containing generated paths and statistics about the generation process
 */
export function algo_1_privateTutor(
	N: number,
	distances: number[][],
	times: number[][],
	targetSum: number,
	dailyTimeLimit: number,
	timeSpentInNodes: number[]
)
{
	let stats = {
		pathsGenerated: 0,
		pathsAfterTimeConstraints: 0,
		pathsAfterDistanceConstraints: 0,
	}
	validateAlgo1Inputs(N, distances, times, targetSum, dailyTimeLimit, timeSpentInNodes)

	// Get initial set of random paths using certain rules
	let paths = findNRandomPaths_1_privateTutor(N, distances.length)
	stats.pathsGenerated = paths.length

	// apply the rest of the rules to the paths
	paths = dropImpossiblePaths(paths, distances)

	// Limit trips by time constraints
	paths = limitTripsTimes(times, paths, dailyTimeLimit, timeSpentInNodes)
	stats.pathsAfterTimeConstraints = paths.length
	
	// Calculate total distance and trim if needed
	let { trips, totalDistance } = enrich2(paths, distances)
	stats.pathsAfterDistanceConstraints = trips.length // set for early return

	if (totalDistance <= targetSum)
		return { paths, stats }

	// Drop trips until we're under targetSum
	while (trips.length > 0 && totalDistance > targetSum)
	{
		let idx = Math.floor(Math.random() * trips.length)
		let trip = trips[idx]!
		let tripDistance = trip.distance
		trips.splice(idx, 1)
		totalDistance -= tripDistance
	}
	stats.pathsAfterDistanceConstraints = trips.length // update

	return { paths: trips.map(t => t.trip), stats }
}

function enrich2(
	trips: number[][],
	distances: number[][],
	// timeMatrix: number[][],
	// timeSpentInNodes: number[],
)
{
	let enriched = trips.map(t => ({
		trip: t,
		distance: pathDistance(distances, t),
	}))
	return {
		trips: enriched,
		totalDistance: enriched.reduce((sum, t) => sum + t.distance, 0),
	}
}

export function findNRandomPaths_1_privateTutor(
	N: number,
	nodesCount: number,
)
{
	// 1. private tutor always starts in the root node
	// 2. private tutor always ends in the root node
	// 3. private tutor can visit any node in the graph once a day
	// 4. private tutor cannot visit the same node multiple times a day

	let p = permsOf(nodesCount - 1, nodesCount - 1) // we use -1 here because we don't want to visit the root node (see below)
	// for each value, generate a path
	let paths: number[][] = []
	while (paths.length < N)
	{
		let path = p.sample()
		if (!path)
			continue

		// we have excluded the root node earlier, so now
		// we have something like [1, 2, 0]
		// but we don't have any root nodes here, so 0 is not a root node, we shift them by 1
		// [1, 2, 0] now means visited node 2, then node 3, then node 1

		// add +1 to each node to account for the root node
		path = path.map(p => p + 1)
		// add the root node to the beginning and end of each path
		paths.push([0, ...path, 0])
	}
	// return the paths
	return paths
}

function dropImpossiblePaths(
	paths: number[][],
	distances: number[][]
): number[][]
{
	return paths.map(path =>
	{
		for (let i = 0; i < path.length - 1; i++)
		{
			let from = path[i]
			let to = path[i + 1]
			// if we can't travel from one node to another, drop the path
			if (distances[from!]![to!]! === Infinity)
			{
				// if this is the first node, drop the entire path
				if (i === 0)
					return []

				// otherwise, drop the rest of the path
				return path.slice(0, i)
			}
		}
		return path
	})
}

function* findPossiblePath<T>(
	initialState: T,
	start: number,
	findNext: (from: number, MUTABLE_state: T) => number,
): Generator<number, void, unknown>
{
	let currentNode = start
	let mutableState = initialState
	// Yield the starting point
	yield currentNode

	while (true)
	{
		// Get next node using the provided findNext function
		const nextNode = findNext(currentNode, mutableState)

		// If findNext returns -1, stop the sequence
		if (nextNode < -1)
			break

		// Update previous and current nodes
		currentNode = nextNode

		// Yield the next node in the sequence
		yield currentNode
	}
}
// Common types
type ExactlyOnceState = {
	idx: number
	order: number[]
}

type AtMostOnceReachableState = {
	idx: number
	order: number[]
	reachable: boolean[][]
}

type UntilMaxDistTimeState = {
	idx: number
	order: number[]
	distances: number[][]
	times: number[][]
	timesInNodes: number[]
	maxDistance: number
	maxTime: number
	curDistance: number
	curTime: number
}

// 1. exactlyOnce - visits every node exactly once in the provided order
function exactlyOnce(from: number, state: ExactlyOnceState): number
{
	if (state.idx >= state.order.length)
		return -1 // End of sequence

	return state.order[state.idx++]!
}

// 2. atMostOnceReachable - visits nodes in order if reachable
function atMostOnceReachable(
	from: number,
	state: AtMostOnceReachableState
): number
{
	while (state.idx < state.order.length)
	{
		const nextNode = state.order[state.idx]

		// Skip if current node can't reach the next node
		if (!state.reachable[from]![nextNode!])
		{
			state.idx++
			continue
		}

		return nextNode!
	}
	return -1
}

// 3. untilMaxDistTime - visits nodes until max distance/time is reached
function untilMaxDistTime(
	from: number,
	state: UntilMaxDistTimeState
): number
{
	while (state.idx < state.order.length)
	{
		let nextNode = state.order[state.idx]

		// Calculate new distance and time if we visit this node
		let newDistance = state.curDistance + state.distances[from]![nextNode!]!
		let newTime = state.curTime + state.times[from]![nextNode!]! + state.timesInNodes[nextNode!]!

		// Skip if exceeds limits
		if (newDistance > state.maxDistance || newTime > state.maxTime)
		{
			state.idx++
			continue
		}
		// Update current distance and time
		state.curDistance = newDistance
		state.curTime = newTime
		return nextNode!
	}
	return -1
}

export function limitTripsTimes(
	timeMatrix: number[][],
	trips: number[][],
	dailyTimeLimit: number,
	timeSpentInNodes: number[]
): number[][]
{
	return trips.map(trip => limitTripTime(trip, timeMatrix, timeSpentInNodes, dailyTimeLimit))
}

/**
 * Limits a trip to fit within the daily time limit by removing nodes if needed
 * @param trip Array of nodes representing the trip path
 * @param timeMatrix Matrix of travel times between nodes
 * @param timeSpentInNodes Time spent at the corresponding node during visit
 * @param dailyTimeLimit Maximum allowed time for the trip
 * @returns Modified trip that fits within the daily time limit
 */
export function limitTripTime(trip: number[], timeMatrix: number[][], timeSpentInNodes: number[], dailyTimeLimit: number): number[]
{
	// Return empty trip if input is empty
	if (!trip.length)
		return trip

	let totalTime = 0
	let validTrip: number[] = []
	let prev = -1 // Track previous node to calculate travel time

	// stage 1: go through the trip and remove nodes that exceed time limit

	// Iterate through each node in the trip
	for (let node of trip)
	{
		// skip first node
		if (prev == -1)
		{
			prev = node
			validTrip.push(node)
			continue
		}

		// Calculate time to travel from previous node
		let travelTime = timeBetweenNodes(prev, node, timeMatrix)
		let timeSpentInNode = timeSpentInNodes[node]!
		// Check if adding this node would exceed daily limit
		let newTotalTime = totalTime + travelTime + timeSpentInNode
		if (newTotalTime > dailyTimeLimit)
		{
			break
		}
		validTrip.push(node)
		// Add travel time and time spent at node
		totalTime = newTotalTime
		prev = node
	}

	// check if any trips are made at all
	if (validTrip.length == 0)
		return []

	// check if we ended in the root node
	// this means that the trip is complete and it fits into time and we can return it
	if (last(validTrip) == 0)
		return removeRootLoops(validTrip)

	// add the root node to the end of the trip
	totalTime += timeBetweenNodes(last(validTrip)!, 0, timeMatrix)
	validTrip.push(0)

	// stage 2: return to root node within time limit

	return limitTripTime_stage2(validTrip, timeMatrix, timeSpentInNodes, dailyTimeLimit, totalTime)
}

export function limitTripTime_stage2(
	validTrip: number[],
	timeMatrix: number[][],
	timeSpentInNodes: number[],
	dailyTimeLimit: number,
	totalTime: number
): number[]
{
	if (validTrip.length == 0)
		return []

	if (validTrip[0] != 0)
		throw new Error('First node must be the root node')
	if (last(validTrip) != 0)
		throw new Error('Last node must be the root node')

	if (validTrip.length <= 2)
		return []

	// check if time is already within limits
	if (totalTime <= dailyTimeLimit)
		return removeRootLoops(validTrip)

	// remove last node until we fit into limits
	while (validTrip.length > 1)
	{
		let lastNode = validTrip.pop()!
		let timeSpentInNode = timeSpentInNodes[lastNode]!

		let newLastNode = last(validTrip)!
		let timeToLastNode = timeBetweenNodes(newLastNode, lastNode, timeMatrix)

		// Calculate new total time
		totalTime = totalTime - timeSpentInNode - timeToLastNode

		let newReturnTime = timeBetweenNodes(newLastNode, 0, timeMatrix)

		if (totalTime + newReturnTime <= dailyTimeLimit)
		{
			validTrip.push(0)
			totalTime += newReturnTime
			break
		}
	}

	return removeRootLoops(validTrip)
}
function removeRootLoops(trip: number[])
{
	// check that we're not just going to the root node and back

	if (trip[0] != 0)
		return trip
	if (last(trip) != 0)
		return trip

	if (trip.length <= 2)
		return []

	return trip
}

function timeBetweenNodes(
	from: number,
	to: number,
	timeMatrix: number[][]
): number
{
	return timeMatrix[from]![to]!
}

export function algo_2_singleWarehouseDelivery()
{
	/*
		2. Офис - Клиент - Офис - Клиент…
		client->client is always infinity
	*/
}

export function algo_3_randomDelivery()
{
	/*
		3. Офис - Поставщик - Получатель - Поставщик - Получатель…
		supplier->supplier and customer->customer is always infinity
	*/
}
